import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PauseCircle, PlayCircle } from "lucide-react";

export const PAUSED = ["On Hold", "Blocked", "Inactive"];
export const HOLD_LABEL = { "On Hold": "On Hold", Blocked: "Blocked (client unresponsive)", Inactive: "Inactive (client inactive)" };

export function HoldReasonBanner({ app }) {
  if (!PAUSED.includes(app.status)) return null;
  return (
    <div data-testid="case-hold-banner" className="mt-1.5 max-w-xl text-xs rounded-lg bg-slate-100 border border-slate-200 text-slate-700 px-2.5 py-1.5">
      <b>{HOLD_LABEL[app.status]}</b>{app.hold_by ? ` · by ${app.hold_by}` : ""}{app.hold_reason ? ` — “${app.hold_reason}”` : ""}
      <span className="block text-slate-500">No overdue or next-step reminders while paused.</span>
    </div>
  );
}

export function CaseHoldControl({ app, onChange }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("On Hold");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const paused = PAUSED.includes(app.status);

  const hold = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/applications/${app.id}/hold`, { status, reason }); toast.success(`Case set to ${HOLD_LABEL[status]}`); setOpen(false); setReason(""); onChange?.(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/applications/${app.id}/resume`); toast.success(`Case resumed (${data.status})`); onChange?.(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (paused) return (
    <button data-testid="case-resume-btn" onClick={resume} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 disabled:opacity-50">
      <PlayCircle className="h-4 w-4" /> Resume case
    </button>
  );
  return (
    <>
      <button data-testid="case-hold-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-slate-100 text-slate-700 hover:bg-slate-200">
        <PauseCircle className="h-4 w-4" /> Hold / Block
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-md" data-testid="case-hold-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Pause this case</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {["On Hold", "Blocked"].map((s) => (
              <button key={s} type="button" data-testid={`case-hold-option-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setStatus(s)}
                className={`px-3 py-2.5 rounded-xl border text-sm font-semibold ${status === s ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>{HOLD_LABEL[s]}</button>
            ))}
          </div>
          <label className="text-xs font-medium text-slate-500">Reason (optional)</label>
          <textarea data-testid="case-hold-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Waiting for client to arrange funds"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <p className="text-[11px] text-slate-500">Paused cases are left out of overdue and next-step reminders until you resume them.</p>
          <button data-testid="case-hold-confirm" onClick={hold} disabled={busy} className="w-full px-4 py-2.5 rounded-xl bg-secondary text-white text-sm font-semibold disabled:opacity-60">{busy ? "Saving…" : `Set to ${HOLD_LABEL[status]}`}</button>
        </DialogContent>
      </Dialog>
    </>
  );
}
